import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AppointmentIconIcon,
    ChannelsIcon,
    PracticeFrontIcon,
    ProspectsIcon,
    HomeIcon,
    EhrIcon,
    ReputationManagementIcon,
    KlarityReviewsIcon,
    CalendarIcon,
    InboxIcon,
    ProfileIcon,
    TextVisitIcon,
    EFaxIcon,
} from 'components/MenuIcons';
import SubscriptionStore from 'store/Subscription';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Spin, Button, Alert, Popover } from 'antd';
import type { MenuProps } from 'antd';
import s from './s.module.less';
import { PATH } from 'constants/path';
import ProviderStore from 'store/Provider';
import { Provider } from 'types/provider';

import Logo from 'assets/logo/kh.svg';
import LogoMini from 'assets/logo/kh.png';
//import Logo from 'assets/campain/blackfriday2024/kiwiLogo.png';
// import LogoMini from 'assets/campain/blackfriday2024/kiwiLogo_mini.png';
import useLoadBasicData from 'hooks/useLoadBasicData';
import {
    openSubscriptionModal,
    openManageSubscriptionModal,
    openKlaritySubscriptionModal,
    openKiwiPlanAdvertisingModal,
} from 'utils/globalLayerControl';
import { FileTextOutlined, SettingOutlined, LogoutOutlined } from '@ant-design/icons';
import { redirecToLogout } from 'utils/redirecToLogout';
import {
    EDot,
    EKey,
    getHasViewDotPage,
    getHasViewKiwiEhrPage,
    getHasViewKlarityReviewPage,
    getKey,
    setHasViewDotPage,
    setHasViewKiwiEhrPage,
    setHasViewKlarityReviewPage,
    getHasShowHelpCenterPopupForKlarityUser,
    setHasShowHelpCenterPopupForKlarityUser,
} from 'utils/localstore';
import useSubscriptionPlan from 'hooks/useSubscriptionPlan';
import { isFreeUser, isPaidFailUser, shouldShowKlarityPlanUI } from 'utils/provider';
import useGetMyChannels from 'hooks/useGetMyChannels';
import ProviderChannelsStore from 'store/ProviderChannles';
import { getPFStatusData } from 'api/practiceFront';
import useGetUniprofileStatus from 'hooks/useGetUniprofileStatus';
import useGetUniprofileEntry from 'hooks/useGetUniprofileEntry';
import useProviderServiceFlag from 'hooks/useProviderServiceFlag';
import ProviderAgreement from 'components/ProviderAgreement';
import HomeStore from 'store/Home';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { TSubscriptionType } from 'types/subscription';
import useGetKlarityReviewList from 'hooks/useGetKlarityReviewList';
import { EOpenType, ESortType } from 'pages/dashboard/KlarityReviewsDashboradPage/types';
import ReviewStore from 'store/Review';
import AlertStore from 'store/GlobalAlert';
import { setHelpCenterActiveKey } from 'utils/sessionStorage';
import { isLocalhost, shouldUseKlarityUi, isKlarityHost, getKlarityDomain } from 'utils/common';
import useInboxAllUnreadCount from 'hooks/inbox/useInboxAllUnreadCount';
import useBudgetInfo from 'hooks/marketing/useBudgetInfo';
import PPALayout from 'pages/PPALayout';
import ProfilePopover from 'components/ProfilePopover';
import { handleSwitchToAccount } from 'utils/switchAccount';
import { ERegisterType } from 'api/public';

const { Content, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(label: React.ReactNode, key: React.Key, icon?: React.ReactNode, children?: MenuItem[]): MenuItem {
    return {
        key,
        icon,
        children,
        label,
    } as MenuItem;
}

enum MenuKey {
    HOME = 'home',
    APPOINTMENT = 'appointment',
    CHANNELS = 'channels',
    PROSPECTS = 'prospects',
    CLIENTS = 'clients',
    PRACTICE_FRONT = 'practice-front',
    EHR = 'ehr',
    REPUTATION_MANAGEMENT = 'reputation-management',
    KLARITY_REVIEWS = 'klarity-reviews',
    KLARITY_ASSESSMENT = 'klarity-assessment',
    CALENDAR = 'calendar',
    INBOX = 'inbox',
    MARKETING = 'marketing',
    PROFILE = 'profile',
    TEXT_VISIT = 'text-visit',
    EFAX = 'eFax',
}

// if (isLocalhost()) {
//     items.push(getItem('Practice front', MenuKey.PRACTICE_FRONT, PracticeFrontIcon));
//     items.push(getItem('My Prospects', MenuKey.PROSPECTS, ProspectsIcon));
// }

const keyPathMap: Partial<Record<MenuKey, string>> = {
    [MenuKey.EHR]: `${PATH.DASHBOARD}/${PATH.EHR_HOME_PROVIDER}`,
    [MenuKey.HOME]: `${PATH.DASHBOARD}/${PATH.PROVIDER_HOME}`,
    [MenuKey.CHANNELS]: `${PATH.DASHBOARD}/${PATH.CHANNEL}`,
    [MenuKey.APPOINTMENT]: `${PATH.DASHBOARD}/${PATH.APPOINTMENT}`,
    [MenuKey.PRACTICE_FRONT]: `${PATH.DASHBOARD}/${PATH.PRACTICE_FRONT}`,
    [MenuKey.PROSPECTS]: `${PATH.DASHBOARD}/${PATH.PROSPECTS}`,
    [MenuKey.CLIENTS]: `${PATH.DASHBOARD}/${PATH.CLIENTS}`,
    [MenuKey.REPUTATION_MANAGEMENT]: `${PATH.DASHBOARD}/${PATH.REPUTATION_MANAGEMENT}`,
    [MenuKey.KLARITY_REVIEWS]: `${PATH.DASHBOARD}/${PATH.KLARITY_REVIEWS}`,
    [MenuKey.CALENDAR]: `${PATH.DASHBOARD}/${PATH.CALENDAR}`,
    [MenuKey.INBOX]: `${PATH.DASHBOARD}/${PATH.INBOX}`,
    [MenuKey.MARKETING]: `${PATH.DASHBOARD}/${PATH.MARKETING}`,
    [MenuKey.PROFILE]: `${PATH.DASHBOARD}/${PATH.PROFILE}`,
    [MenuKey.TEXT_VISIT]: `${PATH.DASHBOARD}/${PATH.TEXT_VISIT}`,
};

const getDefaultKey = (pathStr?: string): MenuKey => {
    const path = pathStr || window.location.pathname;
    if (path.includes(MenuKey.APPOINTMENT)) {
        //setting also use this
        return MenuKey.APPOINTMENT;
    }
    if (path.includes('channel')) {
        return MenuKey.CHANNELS;
    }
    if (path.includes('ehr')) {
        return MenuKey.EHR;
    }
    if (path.includes('practice-front')) {
        return MenuKey.PRACTICE_FRONT;
    }
    if (path.includes('clients') || path.includes('prospects')) {
        return MenuKey.CLIENTS;
    }
    if (path.includes('clients') || path.includes('prospects')) {
        return MenuKey.CLIENTS;
    }
    if (path.includes('reputation')) {
        return MenuKey.REPUTATION_MANAGEMENT;
    }
    if (path.includes('klarity-reviews')) {
        return MenuKey.KLARITY_REVIEWS;
    }
    if (path.includes('calendar')) {
        return MenuKey.CALENDAR;
    }
    if (path.includes('inbox') || path.includes('ava-setting')) {
        return MenuKey.INBOX;
    }
    if (path.includes('marketing')) {
        return MenuKey.MARKETING;
    }
    if (path.includes('profile')) {
        return MenuKey.PROFILE;
    }
    if (path.includes('text-visit')) {
        return MenuKey.TEXT_VISIT;
    }
    return MenuKey.HOME;
};

const LayoutPage: React.FC = () => {
    const [getGloalAlert, setGlobalAlert] = AlertStore.useStore();
    const [collapsed, setCollapsed] = useState(false);
    const [get] = ProviderStore.useStore();
    const [currentKey, setCurrentKey] = useState<MenuKey>(getDefaultKey());
    const provider: Provider = get('data') as Provider;
    const uniprofileFinished = get('uniprofileStatus');
    const toggleCollapsed = useCallback(() => setCollapsed(!collapsed), [collapsed]);

    // const [down]

    const navigate = useNavigate();
    const location = useLocation();
    const [loadSettingData] = useLoadBasicData();
    const [subscriptionPlan, loadingSubscriptionPlan] = useSubscriptionPlan();

    const [getSubscription] = SubscriptionStore.useStore();
    const currentPlan = getSubscription('currentPlan');

    const isKlarityStarter = currentPlan?.type === TSubscriptionType.KLARITY_STARTER;
    const isFree = isFreeUser();
    const isPaidFail = isPaidFailUser();
    const [myChannels, loadingMychannels, error, refetch] = useGetMyChannels();
    const [, setProviderChannels] = ProviderChannelsStore.useStore();
    const [isOpenUpgrade, setIsOpenUpgrade] = useState(false);
    const [, forceUpdate] = React.useState({});
    const [allUnreadCount, unreadLoading, refetchAllUnreadCount] = useInboxAllUnreadCount();
    const [adBudget, adBudgetStatus] = useBudgetInfo();

    const [flag] = useProviderServiceFlag();
    const isKlarity = !!flag?.klarityServiceFlag;
    const kiwiEhrHighLight = !getHasViewKiwiEhrPage();
    // const prospectHighLight = !getHasViewDotPage(EDot.PROSPECT_0810);
    const marketingHighLight =
        !getHasViewDotPage(EDot.MARKETING_0903) ||
        adBudgetStatus.isBudgetNearlyExhausted ||
        adBudgetStatus.isCostAllBudget;
    const textVisitHighLight = !getHasViewDotPage(EDot.TEXT_VISIT_0923);
    const [homeInfo, loadingHomeInfo, refetchHomeInfo] = useProviderHomeInfo();
    const shouldShowKlarityUi = shouldUseKlarityUi(homeInfo);
    const isShowSwitchAccount = homeInfo?.providerTypes?.length === 2;

    const [prospectHighLight, setProspectHighLight] = useState(false);

    useEffect(() => {
        setProspectHighLight(!getHasViewDotPage(EDot.PROSPECT_0810));
    }, []);

    const defautlReviewListParams = useRef({
        sortType: ESortType.MOST_RECENT,
        publicOrPrivate: EOpenType.BOTH_PUBLIC_AND_PRIVATE,
        size: 10,
        current: 1,
        waitToReply: true,
    });
    const [, , loadingReviewList] = useGetKlarityReviewList(defautlReviewListParams.current);
    const [getReviewStore] = ReviewStore.useStore();
    const klarityReviewWaitToReplyNumber = getReviewStore('klarityReviewWaitToReplyNumber');

    const klarityReviewHighLight =
        !getHasViewKlarityReviewPage() || (klarityReviewWaitToReplyNumber && klarityReviewWaitToReplyNumber > 0);

    const showKlarityPlanUI = shouldShowKlarityPlanUI(homeInfo);

    const showPopForHelpCenter = !getHasShowHelpCenterPopupForKlarityUser() && showKlarityPlanUI;
    const showKlarityUi = shouldUseKlarityUi(homeInfo);

    // Use case 1:the provider is Kiwi only; not listed on Klarity --> Kiwi Reputation
    // Use case 2:Klarity with Kiwi for Klarity --> Klarity Review

    const showInbox = true;
    const showInboxUnreadCount = !!(allUnreadCount && allUnreadCount > 0);

    let items: MenuItem[] = [
        getItem('Home', MenuKey.HOME, HomeIcon),
        showInbox
            ? getItem(
                  showInboxUnreadCount ? (
                      <div className={s.itemWithCount}>
                          Messages
                          <span className={s.unread}>{allUnreadCount > 99 ? '99+' : allUnreadCount}</span>
                      </div>
                  ) : (
                      <span>Messages</span>
                  ),
                  MenuKey.INBOX,
                  InboxIcon,
              )
            : undefined,
        // showKlarityPlanUI ? undefined : getItem('Calendar', MenuKey.CALENDAR, CalendarIcon),
        getItem('Calendar', MenuKey.CALENDAR, CalendarIcon),
        getItem(<span className={prospectHighLight ? s.hightLight : ''}>Clients</span>, MenuKey.CLIENTS, ProspectsIcon),
        getItem('Reputation Management', MenuKey.REPUTATION_MANAGEMENT, ReputationManagementIcon),
        isKlarity
            ? getItem(<span className={kiwiEhrHighLight ? s.hightLight : ''}>Kiwi EHR</span>, MenuKey.EHR, EhrIcon)
            : undefined,
        //getItem(<span className={s.hightLight}>Kiwi EHR</span>, MenuKey.EHR, EhrIcon),
        // getItem('Channels', MenuKey.CHANNELS, ChannelsIcon),
        // getItem('Practice front', MenuKey.PRACTICE_FRONT, PracticeFrontIcon),
        getItem(
            <span className={marketingHighLight ? s.hightLight : ''}>Marketing</span>,
            MenuKey.MARKETING,
            ChannelsIcon,
        ),
        // showKlarityPlanUI ? undefined : getItem('Appointment', MenuKey.APPOINTMENT, AppointmentIconIcon),
        // getItem(<span className={textVisitHighLight ? s.hightLight : ''}>Text Visit</span>, MenuKey.TEXT_VISIT, TextVisitIcon),
    ].filter(Boolean) as MenuItem[];

    if (isLocalhost()) {
        items = [
            getItem('Home', MenuKey.HOME, HomeIcon),
            getItem(
                showInboxUnreadCount ? (
                    <div className={s.itemWithCount}>
                        Messages
                        <span className={s.unread}>{allUnreadCount > 99 ? '99+' : allUnreadCount}</span>
                    </div>
                ) : (
                    <span>Messages</span>
                ),
                MenuKey.INBOX,
                InboxIcon,
            ),
            getItem('Calendar', MenuKey.CALENDAR, CalendarIcon),
            getItem(
                <span className={prospectHighLight ? s.hightLight : ''}>Clients</span>,
                MenuKey.CLIENTS,
                ProspectsIcon,
            ),
            getItem('Reputation Management', MenuKey.REPUTATION_MANAGEMENT, ReputationManagementIcon),
            getItem(<span className={kiwiEhrHighLight ? s.hightLight : ''}>Kiwi EHR</span>, MenuKey.EHR, EhrIcon),
            //getItem(<span className={s.hightLight}>Kiwi EHR</span>, MenuKey.EHR, EhrIcon),
            // getItem('Channels', MenuKey.CHANNELS, ChannelsIcon),
            // getItem('Practice front', MenuKey.PRACTICE_FRONT, PracticeFrontIcon),
            getItem(
                <span className={marketingHighLight ? s.hightLight : ''}>Marketing</span>,
                MenuKey.MARKETING,
                ChannelsIcon,
            ),
            // showKlarityPlanUI ? undefined : getItem('Appointment', MenuKey.APPOINTMENT, AppointmentIconIcon),
            getItem(
                <span className={klarityReviewHighLight ? s.hightLight : ''}>Klarity reviews</span>,
                MenuKey.KLARITY_REVIEWS,
                KlarityReviewsIcon,
            ),
            getItem('Profile', MenuKey.PROFILE, ProfileIcon),
            getItem('eFax', MenuKey.EFAX, EFaxIcon),
        ];
    }

    const [, loading] = useGetUniprofileStatus();
    const [uniprofileEntryPath] = useGetUniprofileEntry();

    React.useEffect(() => {
        //openManageSubscriptionModal();
    }, []);

    const ProviderMenus = useMemo(
        () => [
            {
                key: '1',
                label: (
                    // <Link to={`${PATH.CREATE_UNI_PROFILE}?type=uniprofile`}>
                    <Link to={`${uniprofileEntryPath}?type=uniprofile`}>
                        <FileTextOutlined />
                        <span style={{ marginLeft: '8px' }}>UniProfile</span>
                    </Link>
                ),
            },
            {
                key: '2',
                label: (
                    <a onClick={() => openManageSubscriptionModal()}>
                        <SettingOutlined />
                        <span style={{ marginLeft: '8px' }}>Setting</span>
                    </a>
                ),
            },
            {
                key: '3',
                label: (
                    <a
                        onClick={() => {
                            const key = getKey(EKey.BOX_PRACTICE_TOKEN);
                            localStorage.removeItem(key);

                            redirecToLogout();
                        }}
                    >
                        <LogoutOutlined />
                        <span style={{ marginLeft: '8px' }}>Logout</span>
                    </a>
                ),
            },
        ],
        [uniprofileFinished],
    );

    useEffect(() => {
        loadSettingData();
        setProviderChannels('data', myChannels);
    }, [loadSettingData, myChannels]);

    const onClickMenu = ({ key }: { key: string }) => {
        const path: string | undefined = keyPathMap[key as MenuKey];
        if (key === MenuKey.EHR) {
            setHasViewKiwiEhrPage('true');
        }
        if (key === MenuKey.PROSPECTS || key === MenuKey.CHANNELS) {
            setHasViewDotPage(EDot.PROSPECT_0810, 'true');
            setProspectHighLight(false);
        }
        if (key === MenuKey.MARKETING) {
            setHasViewDotPage(EDot.MARKETING_0903, 'true');
        }
        if (key === MenuKey.TEXT_VISIT) {
            setHasViewDotPage(EDot.TEXT_VISIT_0923, 'true');
        }
        if (key === MenuKey.KLARITY_REVIEWS) {
            setHasViewKlarityReviewPage('true');
        }
        if (path) {
            navigate(path);
            setCurrentKey(key as MenuKey);
        }
        refetchAllUnreadCount();
    };

    useEffect(() => {
        const newKey = getDefaultKey(location.pathname);
        setCurrentKey(newKey);
    }, [location]);

    // If only KLARITY_PORTAL account and not on localhost, redirect to Klarity domain dashboard
    React.useEffect(() => {
        if (
            !loadingHomeInfo &&
            homeInfo?.providerTypes?.length === 1 &&
            homeInfo?.providerTypes[0] === ERegisterType.KLARITY_PORTAL &&
            !isLocalhost() &&
            !isKlarityHost()
        ) {
            // false表示切换到Klarity账号
            navigate(`${PATH.SWITCH_ACCOUNT_NOTICE}?accountType=${ERegisterType.KLARITY_PORTAL}`);
        }
        if (
            !loadingHomeInfo &&
            homeInfo?.providerTypes?.length === 1 &&
            homeInfo?.providerTypes[0] === ERegisterType.KIWI_PORTAL &&
            !isLocalhost() &&
            isKlarityHost()
        ) {
            // true表示切换到kiwi账号
            navigate(`${PATH.SWITCH_ACCOUNT_NOTICE}?accountType=${ERegisterType.KIWI_PORTAL}`);
        }
    }, [homeInfo, loadingHomeInfo]);

    const handleUpgradeClick = useCallback(() => {
        if (showKlarityPlanUI) {
            openKlaritySubscriptionModal();
        } else {
            openSubscriptionModal();
            //openKiwiPlanAdvertisingModal();
        }
    }, [showKlarityPlanUI]);

    const handleCloseGlobalAlert = useCallback(() => {
        setGlobalAlert('info', { show: false, message: '', type: 'success' });
    }, []);

    if (loadingSubscriptionPlan === null || loadingSubscriptionPlan === true || loadingMychannels === null || loading) {
        return (
            <div className={s.spinnerWrap}>
                <Spin />
            </div>
        );
    }

    const updateBenifit: string[] = [
        'Access to more channels',
        'AI-Powered prospect management',
        'Reputation management',
        'Advanced website builder',
    ];

    const showDiscount = false;

    if (shouldShowKlarityUi) {
        return <PPALayout homeInfo={homeInfo} />;
    }

    return (
        <Layout className={s.layout}>
            <ProviderAgreement />
            <Sider theme="light" className={s.sider} collapsed={collapsed} width="255px">
                <div className={s.siderContent}>
                    <div className={s.menuHead}>
                        {collapsed && (
                            <img
                                onClick={() => {
                                    toggleCollapsed();
                                }}
                                src={LogoMini}
                                className={s.logoS}
                            />
                        )}
                        {!collapsed && (
                            <img
                                onClick={() => {
                                    toggleCollapsed();
                                }}
                                src="https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/ui/logo_kiwihealth_by_klarity_380x96.png"
                                //src={Logo}
                                className={s.logo}
                            />
                        )}
                    </div>
                    <Menu
                        onClick={onClickMenu}
                        key={currentKey}
                        defaultSelectedKeys={[getDefaultKey()]}
                        mode="inline"
                        items={items}
                    />
                </div>
                <div className={s.siderFooter}>
                    <div className={s.footerMenu}>
                        {/* {(isFree || isKlarityStarter) &&
                            !isPaidFail &&
                            !collapsed &&
                            currentPlan && (
                            <div className={`${s.upgradeWrap} ${isOpenUpgrade ? s.upgradeWrapOpen : ''}`}>
                                <div className={s.uTitle}>
                                    <div className={s.uLeft}>
                                        Upgrade now
                                        {showDiscount && (
                                            <span>
                                                {' '}
                                                to elevate your practice
                                                with our early bird offer;
                                                save
                                                <span
                                                    className={s.highLight}
                                                >
                                                    {' '}
                                                    20%
                                                </span>
                                                !
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        onClick={() => {
                                            setIsOpenUpgrade(
                                                !isOpenUpgrade,
                                            );
                                        }}
                                        className={s.uRight}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M5 15L12 8L19 15"
                                                stroke="#6B7280"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            )} */}
                        <div className={`${s.menuItem} ${s.withoutBottomGap}`}>
                            {showPopForHelpCenter && (
                                <Popover
                                    placement="right"
                                    rootClassName={s.popoverContent}
                                    content={
                                        <div className={s.popContent}>
                                            <div className={s.popTitle}>New! Access Klarity policies & resources</div>
                                            <div className={s.popDesc}>
                                                Find Klarity policies, training materials, and helpful guides under
                                                “Need help?”. Stay informed and manage your practice with ease.
                                            </div>
                                            <div className={s.popBtn}>
                                                <Button
                                                    type="primary"
                                                    onClick={() => {
                                                        setHelpCenterActiveKey('2');
                                                        navigate(`${PATH.DASHBOARD}/${PATH.HELP_CENTER}`);
                                                    }}
                                                >
                                                    Learn more
                                                </Button>
                                                <Button
                                                    type="text"
                                                    className={s.btnText}
                                                    onClick={() => {
                                                        setHasShowHelpCenterPopupForKlarityUser('true');
                                                        forceUpdate({});
                                                    }}
                                                >
                                                    Dismiss
                                                </Button>
                                            </div>
                                        </div>
                                    }
                                    title={null}
                                    open={showPopForHelpCenter}
                                    trigger="hover"
                                >
                                    <div
                                        className={s.needHelp}
                                        onClick={() => navigate(`${PATH.DASHBOARD}/${PATH.HELP_CENTER}`)}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                d="M6.22766 7C6.77678 5.83481 8.25843 5 10.0001 5C12.2092 5 14.0001 6.34315 14.0001 8C14.0001 9.39944 12.7224 10.5751 10.9943 10.9066C10.4519 11.0106 10.0001 11.4477 10.0001 12M10 15H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                                                stroke="#00816B"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        {!collapsed && <span>Need help?</span>}
                                    </div>
                                </Popover>
                            )}
                            {!showPopForHelpCenter && (
                                <div
                                    className={s.needHelp}
                                    onClick={() => {
                                        if (isKlarity) {
                                            setHelpCenterActiveKey('2');
                                        }
                                        navigate(`${PATH.DASHBOARD}/${PATH.HELP_CENTER}`);
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M6.22766 7C6.77678 5.83481 8.25843 5 10.0001 5C12.2092 5 14.0001 6.34315 14.0001 8C14.0001 9.39944 12.7224 10.5751 10.9943 10.9066C10.4519 11.0106 10.0001 11.4477 10.0001 12M10 15H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                                            stroke="#00816B"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    {!collapsed && <span>Need help?</span>}
                                </div>
                            )}
                        </div>
                        <div className={s.menuItem}>
                            <ProfilePopover
                                provider={provider}
                                userType="kiwi"
                                photoClassName={s.profilePhoto}
                                isShowSwitchAccount={isShowSwitchAccount}
                            />
                            {!collapsed && (
                                <div className={s.profileName}>{provider?.firstName || provider?.email}</div>
                            )}
                            <Dropdown className={s.dropDown} menu={{ items: ProviderMenus }} trigger={['click']}>
                                <div className={s.moreActions} />
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </Sider>
            <Layout className={s.siteLayout} id="siteLayout">
                <Spin spinning={loadingSubscriptionPlan || loadingMychannels || loadingReviewList}>
                    {getGloalAlert('info')?.show && (
                        <Alert
                            className={s.globalAlert}
                            message={getGloalAlert('info')?.message || ''}
                            type={getGloalAlert('info')?.type || 'success'}
                            closable
                            onClose={handleCloseGlobalAlert}
                        />
                    )}
                    <Content>
                        <Outlet />
                    </Content>
                </Spin>
            </Layout>
        </Layout>
    );
};

export default LayoutPage;
